import { Button, Classes, H3, Intent, Overlay, Popover, Radio, RadioGroup } from "@blueprintjs/core";
import { DatePicker } from "@blueprintjs/datetime";
import classNames from "classnames";
import moment from 'moment-timezone';
import * as React from "react";
import { DEFAULT_TIME_ZONE, ERROR_TOAST_ICON } from '../../../utils/Constants';
import { formatDateTime, formatTimeInput, getMinDate } from "../../../utils/helpers";
import { isAllEmpty, isBlank, formateDateTimeLabels, getSelectedRadio, setDisabledRadio, shouldBeShownByRadio,
mergeFormatTZ, formatForCalendar } from "../../../utils/emulateEventsHelpers";
import { getDistanceMatrix } from "../../services/googleApiService";
import { postEvent } from '../../services/tripApiService';
import {
	emulateDialogLabel, emulateDialogSubtitle, emulateEventsInput, emulateEventsMainRow, emulateInputStyle, emulateMainGrid,
	eventHeader, labelDbSavedStyle, overlayDivContainer, overlayFooterContainer, tablePopoverColors, textAlignCenter,
	timeZoneAlign
} from '../../styles';
import { AppToaster } from "../../toastComponent";

const OVERLAY_EXAMPLE_CLASS = "docs-overlay-example-transition";
const OVERLAY_TALL_CLASS = "docs-overlay-example-tall";

class OverlayEmulateEvents extends React.PureComponent {

	state = {
		calendarVisible: false,
		isOpen: true,
		isLoadingSave: false,
		isLoadingGenerateTimes: false,
		useTallContent: false,
		timeZone: '',
		minDateTimevalue: moment(),
		inputs: this.defineInputs(),
		lastSavedEvent: 'onsiteEventTime',
		selectedRadio: '',
		isGeneratedTimes: false
	};

	defineInputs() {
		// onsiteEventTime -> Onsite
		// pickupEventTime -> Onboard
		// noShowEventTime -> Noload
		// dropoffEventTime -> Complete
		return [
			{
				name: 'onsiteEventTime', disable: false, value: null, calendarVisible: false, label: 'Onsite',
				enableInputs: ['pickupEventTime', 'noShowEventTime'],
				disableInputs: [],
				greaterThan: [],
				dbSaved: false,
				reEnableInputs: [],
				reDisableInputs: ['pickupEventTime', 'noShowEventTime'],
				canBeSaved: false,
			},
			{
				name: 'pickupEventTime', disable: false, value: null, calendarVisible: false, label: 'Onboard',
				enableInputs: ['dropoffEventTime'],
				disableInputs: ['onsiteEventTime', 'noShowEventTime'],
				greaterThan: ['onsiteEventTime'],
				dbSaved: false,
				reEnableInputs: ['onsiteEventTime', 'noShowEventTime'],
				reDisableInputs: ['dropoffEventTime'],
				canBeSaved: false,
			},
			{
				name: 'noShowEventTime', disable: false, value: null, calendarVisible: false, label: 'Noload',
				enableInputs: [],
				disableInputs: ['pickupEventTime', 'onsiteEventTime'],
				greaterThan: ['onsiteEventTime'],
				dbSaved: false,
				reEnableInputs: ['onsiteEventTime', 'pickupEventTime'],
				reDisableInputs: [],
				canBeSaved: false,
			},
			{
				name: 'dropoffEventTime', disable: false, value: null, calendarVisible: false, label: 'Complete',
				enableInputs: [],
				disableInputs: ['pickupEventTime'],
				greaterThan: ['pickupEventTime'],
				dbSaved: false,
				reEnableInputs: ['pickupEventTime'],
				reDisableInputs: [],
				canBeSaved: false,
			},
		];
	}

	componentDidMount() {
		const inputs = this.state.inputs;
		let minDT = null;
		const pickupTimeZone = this.props.trip.pickupTimeZone === null ? DEFAULT_TIME_ZONE : this.props.trip.pickupTimeZone;
		let lastSavedEvent = 'onsiteEventTime';
		this.enableInputs(['onsiteEventTime'], ['pickupEventTime', 'noShowEventTime', 'dropoffEventTime'], inputs);

		inputs.forEach(input => {
			if (this.props.trip[input.name] !== undefined && this.props.trip[input.name] !== null) {
				input.value = moment.tz(this.props.trip[input.name], pickupTimeZone);
				minDT = input.value;
				lastSavedEvent = input.name;
				input.dbSaved = true;
				this.enableInputs(input.enableInputs, input.disableInputs, inputs);
			}
		});

		inputs.find(input => input.name === lastSavedEvent).canBeSaved = true;
		const selectedRadio = getSelectedRadio(inputs);

		this.setState({
			timeZone: pickupTimeZone,
			minDateTimevalue: minDT === null ? moment.tz(this.props.trip.dueDateTime, pickupTimeZone).startOf('day') : minDT,
			inputs: inputs,
			lastSavedEvent: lastSavedEvent,
			selectedRadio: selectedRadio,
			disabledRadio: setDisabledRadio(selectedRadio, inputs)
		})
	}

	// Radio Logic
	handleRadioChange = (e) => {
		const inputs = this.state.inputs;
		inputs.forEach(input => {
			if (!input.dbSaved) {
				input.value = null;
				const timeInput = document.getElementById(input.name);

				if (timeInput) {
					timeInput.value = null;
				}

				//we just want to disable depending on which we last saved
				this.enableInputs([this.state.lastSavedEvent], input.reDisableInputs, inputs);
			}
		})

		this.setState({
			selectedRadio: e.target.value,
			inputs: inputs,
		});
		this.forceUpdate();
	}

	enableInputs = (enableNames, disableNames, inputs) => {
		const lastSavedEvent = this.state.lastSavedEvent;

		if (enableNames) {
			enableNames.forEach(name => {
				const input = inputs.find(i => i.name === name);
				input.disable = input.dbSaved === true && input.name !== lastSavedEvent ? true : false;

				if (lastSavedEvent === 'pickupEventTime' && input.name === 'noShowEventTime') {
					input.disable = true;
				}
				if (lastSavedEvent === 'noShowEventTime' && input.name === 'pickupEventTime') {
					input.disable = true;
				}
			});
		}

		disableNames.forEach(name => {
			inputs.find(input => input.name === name).disable = true;
		});
	}

	handleTimeChange = async (e, name) => {
		const inputs = this.state.inputs;
		const currentInput = inputs.find(input => input.name === name);
		if (currentInput.value === null) {
			AppToaster.show({ message: `${currentInput.label} Date is required`, intent: "danger", timeout: '7000', icon: ERROR_TOAST_ICON });
			return;
		}

		const selectedDateTime = mergeFormatTZ(e.currentTarget.value, currentInput.value, this.state.timeZone);

		const validatedDateTime = this.validateSelectedTime(selectedDateTime, currentInput.greaterThan);

		currentInput.value = validatedDateTime;
		currentInput.canBeSaved = true;

		const timeInput = document.getElementById(name);
		timeInput.value = formatTimeInput(validatedDateTime);

		const enableInput = currentInput.enableInputs;
		const disableInputs = currentInput.disableInputs;
		this.enableInputs(enableInput, disableInputs, inputs);

		await this.setState({
			inputs: inputs,
		});

		this.forceUpdate();
	}

	validateSelectedTime = (selectedDateTime, greaterThan) => {
		let resDateTime = selectedDateTime;

		if (resDateTime) {
			greaterThan.forEach(g => {
				const input = this.state.inputs.find(i => i.name === g);
				if (moment.tz(input.value, this.state.timeZone).isAfter(selectedDateTime)) {
					resDateTime = moment.tz(input.value, this.state.timeZone);
				}
			})
		} else {
			resDateTime = moment.tz(this.state.minDateTimevalue, this.state.timeZone);
		}

		return resDateTime;
	}

	handleConfirm = async () => {
		try {
			await this.setState({
				isLoadingSave: true,
			});

			if (isAllEmpty(this.state.inputs)) {
				AppToaster.show({ message: `Must complete at least one event.`, intent: "danger", icon: ERROR_TOAST_ICON, });
				return;
			}

			if (isBlank(this.state.inputs)) {
				AppToaster.show({ message: `Previously recorded events cannot be left blank.`, intent: "danger", icon: ERROR_TOAST_ICON, });
				return;
			}

			for (const input of this.state.inputs) {
				if ((input.canBeSaved || this.state.isGeneratedTimes) && input.value) {
					const currentTimeZone = moment.tz(this.state.timeZone).format('Z'); //Gets the offset
					const newDateTime = `${formatDateTime(input.value)} ${currentTimeZone}`;

					await postEvent(
						this.props.trip.tripNumber,
						input.label,
						newDateTime,
						0,
						0
					);
				}

			}

			AppToaster.show({ message: 'Events saved', intent: "success", icon: 'tick', });
			this.handleClose();
		}
		catch (error) {
			console.log(error);
			AppToaster.show({ message: `There was an error while saving the event(s). Please try again.`, intent: "danger", timeout: '7000', icon: 'warning-sign' });

		} finally {
			this.setState({
				isLoadingSave: false,
			});
		}
	}

	handleInteraction = async (state, name) => {
		const inputs = this.state.inputs;

		const input = inputs.find(i => i.name === name);

		if (input.disable) {
			return;
		}

		input.calendarVisible = state;

		await this.setState({
			inputs: inputs,
		});

		this.forceUpdate();
	}

	handleDateChange = (date, name) => {
        const inputs = this.state.inputs;
        const input = inputs.find(i => i.name === name);
		let inputDate;
        if (input.value === null) {
			inputDate = date.getFullYear() + '-' + ("0" + (date.getMonth() + 1)).slice(-2) + '-' + ("0" + date.getDate()).slice(-2) + ' 00:00';
        } else {
            const month = parseInt(date.getMonth(), 10);
            const day = parseInt(date.getDate(), 10);
			input.value.set({ 'month': month, 'date': day });
			inputDate = input.value;
		}
		const minDateTime = moment.tz(this.state.minDateTimevalue, this.state.timeZone);
		const newSelectedDateTime = mergeFormatTZ(formatTimeInput(minDateTime), inputDate, this.state.timeZone);
		const validatedDateTime = this.validateSelectedTime(newSelectedDateTime, input.greaterThan);
		input.value = validatedDateTime;

		//Overwrite the timeinput value to it's minimum time
        const timeInput = document.getElementById(name);
        timeInput.value = formatTimeInput(input.value);
        input.canBeSaved = true;
        const enableInput = input.enableInputs;
        const disableInputs = input.disableInputs;
        this.enableInputs(enableInput, disableInputs, inputs);
        this.setState({
            inputs: inputs,
        });
        this.forceUpdate();
    }

	getMinDate = (greaterThan) => {
		var minDate = getMinDate(this.props.trip.dueDateTime);

		greaterThan.forEach(g => {
			const input = this.state.inputs.find(i => i.name === g)
			if (input.value != null) {
				if (minDate === '') {
					minDate = input.value;
				}
				else {
					const inputValueOf = input.value.valueOf();
					const minDateValueOf = minDate.valueOf();

					if (inputValueOf > minDateValueOf) {
						minDate = input.value;
					}
				}
			}
		})

		if (minDate.valueOf() > this.state.minDateTimevalue.valueOf()) {
			this.setState({
				minDateTimevalue: minDate,
			})
		}

		return minDate?.toDate();
	}

	generateTimes = async () => {
		this.setState({
			isLoadingGenerateTimes: true,
		});

		const eta = await this.getEta(this.props.trip);
		const inputs = this.state.inputs;

		inputs.forEach(input => {
			if (input.canBeSaved || !input.dbSaved) {
				switch (input.name) {
					case ('onsiteEventTime'):
						input.value = moment.tz(this.props.trip.dueDateTime, this.state.timeZone).subtract(5, 'minutes');
						this.enableSpecificInput(input, inputs);
						const onsitetTimeInput = document.getElementById('onsiteEventTime');
						onsitetTimeInput.value = formatTimeInput(input.value);
						break;
					case ('pickupEventTime'):
						const onsite = inputs.find(inp => inp.name === "onsiteEventTime");
						input.value = onsite.dbSaved ? moment.tz(onsite.value, this.state.timeZone).add(5, 'minutes') : moment.tz(this.props.trip.dueDateTime, this.state.timeZone);
						const pickupTimeInput = document.getElementById('pickupEventTime');
						pickupTimeInput.value = formatTimeInput(input.value);
						this.enableSpecificInput(input, inputs);
						break;
					case ('dropoffEventTime'):
						const pickup = inputs.find(inp => inp.name === "pickupEventTime").value;
						const dropoffEventTime = moment.tz(pickup, this.state.timeZone);
						input.value = eta ? dropoffEventTime.add(eta, 'seconds') : dropoffEventTime;
						const dropTimeInput = document.getElementById('dropoffEventTime');
						dropTimeInput.value = formatTimeInput(input.value);
						this.enableSpecificInput(input, inputs);
						break;
					default:
						break;
				}
			}
		})

		this.setState({
			inputs: inputs,
			isLoadingGenerateTimes: false,
			isGeneratedTimes: true
		});

		this.forceUpdate();
	}

	enableSpecificInput = (input, inputs) => {
		const enableInput = input.enableInputs;
		const disableInputs = input.disableInputs;
		this.enableInputs(enableInput, disableInputs, inputs);
	}

	getEta = async (trip) => {
		try {
			if ((trip.pickupEventTime || trip.dueDateTime) && trip.pickupLat !== 0 && trip.pickupLon !== 0 && trip.dropoffLat !== 0 && trip.dropoffLon !== 0) {
				const actualCoordinates = { lat: trip.pickupLat, lon: trip.pickupLon };
				const destinationCoordinates = { lat: trip.dropoffLat, lon: trip.dropoffLon };
				const data = await getDistanceMatrix(actualCoordinates, destinationCoordinates);
				if (data.rows.length > 0) {
					const element = data.rows[0].elements[0];
					if (element.status === "OK") {
						return element.duration.value;
					}
					else {
						return 'N/A';
					}
				} else {
					return 0
				}
			} else {
				return 0;
			}
		}
		catch (error) {
			console.log(error);
			return 0;
		}
	}

	render() {
		const classes = classNames(
			Classes.CARD,
			Classes.ELEVATION_4,
			OVERLAY_EXAMPLE_CLASS,
			localStorage.getItem("themeName"),
			{ [OVERLAY_TALL_CLASS]: this.state.useTallContent },
		);

		return (
			<Overlay className={Classes.OVERLAY_SCROLL_CONTAINER} {...this.state} transitionName={Classes.OVERLAY} onClose={this.handleClose} >
				<div className={classes} style={overlayDivContainer}>
					<H3>Emulate Events</H3>
					<div style={timeZoneAlign}>
						<RadioGroup
							inline
							onChange={(e) => this.handleRadioChange(e)}
							selectedValue={this.state.selectedRadio}
						>
							<Radio disabled={this.state.disabledRadio === 'complete'} label="Complete" value="complete" />
							<Radio disabled={this.state.disabledRadio === 'noload'} label="No load" value="noload" />
						</RadioGroup>
					</div>
					{this.state.selectedRadio !== 'noload' &&
						<div style={eventHeader}>
							<Button onClick={this.generateTimes} loading={this.state.isLoadingGenerateTimes}>Generate Times</Button>
						</div>
					}
					<div style={timeZoneAlign}>
						<strong>Time Zone: {moment.tz(this.state.timeZone).format('z')}</strong>
					</div>
					<div id='emulateGrid' style={emulateMainGrid} >
						{
							this.state.inputs.map(inputStatus => {
								const value = inputStatus.value;
								const shownByRadio = shouldBeShownByRadio(inputStatus.name, this.state.selectedRadio);
								const shouldBeLabel = inputStatus.dbSaved && !inputStatus.canBeSaved;
								const valueToShowInCalendar = !value ? formatForCalendar(this.state.minDateTimevalue, this.state.timeZone) : formatForCalendar(value, this.state.timeZone);

								return (
									<div id='emulateRow' style={emulateEventsMainRow} key={inputStatus.name}>
										{shownByRadio &&
											<div style={emulateDialogSubtitle}>
												<label style={emulateDialogLabel}>
													<strong>{inputStatus.label}</strong>
												</label>
											</div>
										}
										{shownByRadio && !shouldBeLabel &&
											<div style={emulateInputStyle} className="md-form mx-5 my-5" >
												<Popover
													style={tablePopoverColors} isOpen={inputStatus.calendarVisible}
													position={"bottom-left"} onInteraction={(state) => this.handleInteraction(state, inputStatus.name)}
													content={
														<DatePicker className={Classes.ELEVATION_1} canClearSelection={false}
															value={valueToShowInCalendar}
															onChange={(date) => this.handleDateChange(date, inputStatus.name)} highlightCurrentDay={true}
															minDate={this.getMinDate(inputStatus.greaterThan)}
														/>}
													target={<input style={textAlignCenter} className="form-control" size="10"
														value={value === null ? '' : value.format('YYYY-MM-DD')} readOnly disabled={inputStatus.disable} />}
												>
												</Popover>
												<input style={emulateEventsInput} type="time" id={inputStatus.name} className="form-control"
													defaultValue={value === null ? '' : formatTimeInput(value)} disabled={inputStatus.disable}
													onBlur={(e) => this.handleTimeChange(e, inputStatus.name)} name={inputStatus.name}
												/>
												<Button minimal icon="cross" intent='danger' small onClick={() => this.handleReset(inputStatus.name)} disabled={inputStatus.disable} />
											</div>
										}
										{shownByRadio && shouldBeLabel &&
											<div style={labelDbSavedStyle} className="md-form mx-5 my-5" >
												<span><strong>{formateDateTimeLabels(value)}</strong></span>
											</div>
										}
									</div>
								);
							})
						}
					</div>

					<br />
					<div className={Classes.DIALOG_FOOTER_ACTIONS} style={overlayFooterContainer}>
						<Button intent={Intent.SUCCESS} style={{ margin: "" }} onClick={() => this.handleConfirm()} text="Save" loading={this.state.isLoadingSave}
							disabled={this.state.isLoadingGenerateTimes} />
						<Button intent={Intent.NONE} onClick={this.handleClose} style={{ margin: "" }} text="Cancel" disabled={this.state.isLoadingSave} />
					</div>
				</div>
			</Overlay>
		)
	}

	handleOpen = () => {
		this.setState({
			isOpen: true,
		});
	}

	handleClose = () => {
		this.setState({
			isOpen: false,
			isLoadingSave: false,
		});

		this.props.onModalClosed(this.props.id);
	}

	handleReset = (name) => {
		const inputs = this.state.inputs;

		const input = inputs.find(i => i.name === name);

		input.value = null;
		const timeInput = document.getElementById(name);
		timeInput.value = null;
		this.enableInputs(input.reEnableInputs, input.reDisableInputs, inputs);

		this.setState({
			inputs: inputs,
			isGeneratedTimes: false
		});

		this.forceUpdate();
	}
}
export default OverlayEmulateEvents;
